import type {
  ActivityItem,
  NotificationItem,
  Participant,
  ProjectBundle,
  ProjectSection,
  ProjectSummary,
  ProjectTask,
  RiskItem,
  SessionUser,
  TaskFile,
  Visibility,
} from "@/types/models";

export const demoUsers: Record<"internal" | "admin" | "client", SessionUser> = {
  internal: {
    id: "usr-kim",
    displayName: "Kim Nacas",
    email: "owner@vipdesk.local",
    kind: "internal",
    initials: "KN",
    organization: "VIPdesk",
    role: "Project owner",
    canCreateProjects: true,
  },
  admin: {
    id: "demo-vip-admin",
    displayName: "Alex Morgan",
    email: "admin@vipdesk.local",
    kind: "internal",
    initials: "AM",
    organization: "VIPdesk",
    role: "Workspace administrator",
    canCreateProjects: true,
  },
  client: {
    id: "usr-priya",
    displayName: "Priya Shah",
    email: "client@thorne.local",
    kind: "client",
    initials: "PS",
    organization: "Thorne",
    role: "Client lead",
    canCreateProjects: false,
  },
};

export const participants: Participant[] = [
  {
    id: "usr-kim",
    name: "Kim Nacas",
    initials: "KN",
    email: "owner@vipdesk.local",
    organization: "VIPdesk",
    role: "Project owner",
    kind: "internal",
    version: 1,
  },
  {
    id: "usr-jon",
    name: "Jon Bell",
    initials: "JB",
    email: "jon.bell@vipdesk.example",
    organization: "VIPdesk",
    role: "Internal member",
    kind: "internal",
    version: 1,
  },
  {
    id: "usr-maria",
    name: "Maria Santos",
    initials: "MS",
    email: "maria.santos@vipdesk.example",
    organization: "VIPdesk",
    role: "Internal member",
    kind: "internal",
    version: 1,
  },
  {
    id: "usr-priya",
    name: "Priya Shah",
    initials: "PS",
    email: "client@thorne.local",
    organization: "Thorne",
    role: "Client lead",
    kind: "client",
    version: 1,
  },
  {
    id: "usr-owen",
    name: "Owen Wright",
    initials: "OW",
    email: "owen.wright@thorne.example",
    organization: "Thorne",
    role: "Client member",
    kind: "client",
    version: 1,
  },
  {
    id: "usr-lee",
    name: "Lee Park",
    initials: "LP",
    email: "lee.park@thorne.example",
    organization: "Thorne",
    role: "Client member",
    kind: "client",
    state: "pending",
    version: 1,
  },
];

export const projects: ProjectSummary[] = [
  {
    id: "prj_demo0001",
    code: "THO-2607",
    name: "Thorne implementation",
    client: "Thorne",
    description:
      "Launch a coordinated customer care operation across people, process, and technology.",
    ownerUpdate:
      "The operational design is progressing as planned. Technology readiness is at risk while carrier provisioning and integration requirements are confirmed.",
    ownerUpdateBy: "Kim Nacas",
    ownerUpdateAt: "2026-07-30T16:30:00.000Z",
    startDate: "2026-07-17",
    targetEndDate: "2026-12-17",
    workingWeekdays: [1, 2, 3, 4, 5],
    holidays: [],
    progress: 67,
    health: "At risk",
    status: "Active",
    nextMilestone: "Technology ready",
    nextMilestoneDate: "2026-08-24",
    blockers: 2,
    overdue: 1,
    updatedAt: "Today, 10:42 KN",
    targetPolicy: "Hard boundary",
    defaultVisibility: "Shared",
    scheduleVersion: 1,
    version: 1,
  },
  {
    id: "p-northstar",
    code: "NOR-2608",
    name: "Northstar service expansion",
    client: "Northstar Health",
    description: "Expand the service model to two new programs.",
    ownerUpdate:
      "Discovery is on track and the operating model workshop is scheduled.",
    ownerUpdateBy: "Project owner",
    ownerUpdateAt: "2026-07-29T15:18:00.000Z",
    startDate: "2026-08-03",
    targetEndDate: "2026-11-20",
    workingWeekdays: [1, 2, 3, 4, 5],
    holidays: [],
    progress: 28,
    health: "On track",
    status: "Active",
    nextMilestone: "Operating model approved",
    nextMilestoneDate: "2026-08-18",
    blockers: 0,
    overdue: 0,
    updatedAt: "Yesterday, 3:18 PM",
    targetPolicy: "Hard boundary",
    defaultVisibility: "Shared",
    scheduleVersion: 1,
    version: 1,
  },
  {
    id: "p-alder",
    code: "ALD-2604",
    name: "Alder transition",
    client: "Alder & Co.",
    description: "Transition support operations and reporting.",
    ownerUpdate:
      "Handoff preparation is complete and the team is closing final actions.",
    ownerUpdateBy: "Project owner",
    ownerUpdateAt: "2026-07-28T16:06:00.000Z",
    startDate: "2026-04-06",
    targetEndDate: "2026-08-28",
    workingWeekdays: [1, 2, 3, 4, 5],
    holidays: [],
    progress: 91,
    health: "On track",
    status: "Active",
    nextMilestone: "Operational handoff",
    nextMilestoneDate: "2026-08-14",
    blockers: 0,
    overdue: 0,
    updatedAt: "Jul 28, 4:06 PM",
    targetPolicy: "Warn only",
    defaultVisibility: "Shared",
    scheduleVersion: 1,
    version: 1,
  },
];

export const sections: ProjectSection[] = [
  { id: "sec-project", name: "Project management", progress: 72 },
  { id: "sec-design", name: "Operations", progress: 64 },
  { id: "sec-tech", name: "Technology", progress: 38 },
  { id: "sec-reporting", name: "Reporting", progress: 0 },
  { id: "sec-workforce", name: "Workforce management", progress: 0 },
  { id: "sec-recruiting", name: "Recruiting", progress: 0 },
  { id: "sec-finance", name: "Finance", progress: 0 },
  { id: "sec-people", name: "People and culture", progress: 0 },
  { id: "sec-training", name: "Training", progress: 0 },
];

const task = (
  value: Partial<ProjectTask> &
    Pick<ProjectTask, "id" | "sectionId" | "title" | "startDate" | "dueDate">,
): ProjectTask => ({
  projectId: "prj_demo0001",
  level: 0,
  type: "task",
  description: "",
  notes: "",
  ownerId: "usr-kim",
  ownerName: "Kim Nacas",
  ownerInitials: "KN",
  ownerOrganization: "VIPdesk",
  duration: 3,
  status: "Not started",
  health: "On track",
  visibility: "Shared",
  progress: 0,
  predecessorIds: [],
  commentCount: 0,
  fileCount: 0,
  version: 1,
  checklist: [],
  questions: [],
  comments: [],
  ...value,
});

export const tasks: ProjectTask[] = [
  task({
    id: "t-contract",
    sectionId: "sec-project",
    type: "milestone",
    title: "Contract executed",
    startDate: "2026-07-17",
    dueDate: "2026-07-17",
    duration: 0,
    status: "Complete",
    progress: 100,
    ownerName: "VIPdesk / Thorne",
    ownerInitials: "VT",
    visibility: "Shared",
  }),
  task({
    id: "t-kickoff",
    sectionId: "sec-project",
    type: "milestone",
    title: "Project kick-off complete",
    startDate: "2026-07-23",
    dueDate: "2026-07-23",
    duration: 0,
    status: "Complete",
    progress: 100,
    ownerName: "VIPdesk / Thorne",
    ownerInitials: "VT",
    visibility: "Shared",
  }),
  task({
    id: "t-plan-approved",
    sectionId: "sec-project",
    type: "milestone",
    title: "Implementation plan approved",
    startDate: "2026-07-31",
    dueDate: "2026-07-31",
    duration: 0,
    status: "In review",
    progress: 0,
    ownerName: "Kim Nacas",
    ownerInitials: "KN",
    visibility: "Shared",
    predecessorIds: ["t-kickoff"],
    commentCount: 2,
  }),
  task({
    id: "t-op-approved",
    sectionId: "sec-design",
    type: "milestone",
    title: "Operational design approved",
    startDate: "2026-08-14",
    dueDate: "2026-08-14",
    duration: 0,
    ownerName: "Priya Shah",
    ownerInitials: "PS",
    visibility: "Shared",
    predecessorIds: ["t-plan-approved"],
  }),
  task({
    id: "t-tech-ready",
    sectionId: "sec-tech",
    type: "milestone",
    title: "Technology ready",
    startDate: "2026-08-24",
    dueDate: "2026-08-24",
    duration: 0,
    health: "At risk",
    ownerName: "Jon Bell",
    ownerInitials: "JB",
    visibility: "Shared",
    predecessorIds: ["t-op-approved"],
  }),
  task({
    id: "t-training",
    sectionId: "sec-training",
    type: "milestone",
    title: "Training starts",
    startDate: "2026-08-31",
    dueDate: "2026-08-31",
    duration: 0,
    ownerName: "Maria Santos",
    ownerInitials: "MS",
    visibility: "Shared",
    predecessorIds: ["t-tech-ready"],
  }),
  task({
    id: "t-go-live",
    sectionId: "sec-project",
    type: "milestone",
    title: "Go-live",
    startDate: "2026-09-21",
    dueDate: "2026-09-21",
    duration: 0,
    ownerName: "VIPdesk / Thorne",
    ownerInitials: "VT",
    visibility: "Shared",
    predecessorIds: ["t-training"],
  }),
  task({
    id: "t-execute",
    sectionId: "sec-project",
    title: "Execute contract",
    startDate: "2026-07-14",
    dueDate: "2026-07-17",
    duration: 4,
    status: "Complete",
    progress: 100,
    visibility: "Shared",
  }),
  task({
    id: "t-team",
    sectionId: "sec-project",
    type: "summary",
    title: "Identify client project team",
    startDate: "2026-07-17",
    dueDate: "2026-07-22",
    duration: 4,
    status: "Complete",
    progress: 100,
    ownerName: "VIPdesk / Thorne",
    ownerInitials: "VT",
    visibility: "Shared",
  }),
  task({
    id: "t-internal-team",
    sectionId: "sec-project",
    parentId: "t-team",
    level: 1,
    title: "Confirm VIPdesk delivery team",
    startDate: "2026-07-17",
    dueDate: "2026-07-18",
    duration: 2,
    status: "Complete",
    progress: 100,
    visibility: "Internal",
  }),
  task({
    id: "t-client-team",
    sectionId: "sec-project",
    parentId: "t-team",
    level: 1,
    title: "Confirm client project team",
    startDate: "2026-07-20",
    dueDate: "2026-07-22",
    duration: 3,
    status: "Complete",
    progress: 100,
    ownerId: "usr-priya",
    ownerName: "Priya Shah",
    ownerInitials: "PS",
    ownerOrganization: "Thorne",
    visibility: "Shared",
  }),
  task({
    id: "t-kow",
    sectionId: "sec-project",
    title: "Review statement of work",
    startDate: "2026-07-20",
    dueDate: "2026-07-22",
    duration: 3,
    status: "Complete",
    progress: 100,
    visibility: "Internal",
  }),
  task({
    id: "t-plan",
    sectionId: "sec-project",
    type: "summary",
    title: "Implementation plan",
    startDate: "2026-07-23",
    dueDate: "2026-07-31",
    duration: 7,
    status: "In progress",
    progress: 72,
    visibility: "Shared",
  }),
  task({
    id: "t-review-plan",
    sectionId: "sec-project",
    parentId: "t-plan",
    level: 1,
    title: "Review timeline and key dates",
    startDate: "2026-07-23",
    dueDate: "2026-07-27",
    duration: 3,
    status: "Complete",
    progress: 100,
    visibility: "Shared",
  }),
  task({
    id: "t-approve-plan",
    sectionId: "sec-project",
    parentId: "t-plan",
    level: 1,
    title: "Approve implementation plan",
    startDate: "2026-07-28",
    dueDate: "2026-07-31",
    duration: 4,
    status: "In review",
    progress: 80,
    ownerId: "usr-priya",
    ownerName: "Priya Shah",
    ownerInitials: "PS",
    ownerOrganization: "Thorne",
    visibility: "Shared",
    predecessorIds: ["t-review-plan"],
    comments: [
      {
        id: "c-1",
        author: "Priya Shah",
        initials: "PS",
        createdAt: "Today, 9:14 KN",
        body: "The target dates work for our operations team. @Kim Nacas, can you confirm the reporting review is included?",
        visibility: "Shared",
        replies: [
          {
            id: "c-2",
            author: "Kim Nacas",
            initials: "KN",
            createdAt: "Today, 9:38 KN",
            body: "Yes. It is included in Reporting and tied to the readiness milestone.",
            visibility: "Shared",
          },
        ],
      },
    ],
    commentCount: 2,
    fileCount: 1,
    notes:
      "Approval covers the implementation timeline and the client-facing readiness milestones.",
  }),
  task({
    id: "t-approve-checklist",
    sectionId: "sec-project",
    parentId: "t-plan",
    level: 1,
    type: "checklist",
    title: "Confirm implementation-plan approvals",
    startDate: "2026-07-28",
    dueDate: "2026-07-31",
    duration: 4,
    status: "In progress",
    progress: 50,
    ownerId: "usr-priya",
    ownerName: "Priya Shah",
    ownerInitials: "PS",
    ownerOrganization: "Thorne",
    visibility: "Shared",
    predecessorIds: ["t-review-plan"],
    checklist: [
      {
        id: "ck-1",
        label: "Dates reviewed with workstream leads",
        complete: true,
      },
      { id: "ck-2", label: "Client approval recorded", complete: false },
    ],
  }),
  task({
    id: "t-approval-questions",
    sectionId: "sec-project",
    parentId: "t-plan",
    level: 1,
    type: "questionnaire",
    title: "Collect implementation approval details",
    startDate: "2026-07-28",
    dueDate: "2026-07-31",
    duration: 4,
    status: "In review",
    progress: 100,
    ownerId: "usr-priya",
    ownerName: "Priya Shah",
    ownerInitials: "PS",
    ownerOrganization: "Thorne",
    visibility: "Shared",
    predecessorIds: ["t-review-plan"],
    questions: [
      {
        id: "q-1",
        label: "Who gives final approval?",
        type: "Short text",
        required: true,
        answer: "Priya Shah",
      },
    ],
  }),
  task({
    id: "t-cadence",
    sectionId: "sec-project",
    title: "Establish weekly client meeting",
    startDate: "2026-07-24",
    dueDate: "2026-07-28",
    duration: 3,
    status: "Complete",
    progress: 100,
    ownerName: "VIPdesk / Thorne",
    ownerInitials: "VT",
    visibility: "Shared",
  }),
  task({
    id: "t-internal-meet",
    sectionId: "sec-project",
    title: "Establish internal project meetings",
    startDate: "2026-07-24",
    dueDate: "2026-07-28",
    duration: 3,
    status: "Complete",
    progress: 100,
    visibility: "Internal",
  }),
  task({
    id: "t-customer",
    sectionId: "sec-design",
    type: "summary",
    title: "Customer journey",
    startDate: "2026-07-21",
    dueDate: "2026-08-14",
    duration: 19,
    status: "In progress",
    progress: 64,
    ownerId: "usr-priya",
    ownerName: "Priya Shah",
    ownerInitials: "PS",
    ownerOrganization: "Thorne",
    visibility: "Shared",
  }),
  task({
    id: "t-orders",
    sectionId: "sec-design",
    parentId: "t-customer",
    level: 1,
    title: "Review contact drivers",
    startDate: "2026-07-21",
    dueDate: "2026-07-24",
    duration: 4,
    status: "Complete",
    progress: 100,
    ownerId: "usr-owen",
    ownerName: "Owen Wright",
    ownerInitials: "OW",
    ownerOrganization: "Thorne",
    visibility: "Shared",
  }),
  task({
    id: "t-products",
    sectionId: "sec-design",
    parentId: "t-customer",
    level: 1,
    title: "Confirm supported products and services",
    startDate: "2026-07-24",
    dueDate: "2026-07-27",
    duration: 2,
    status: "Complete",
    progress: 100,
    ownerId: "usr-owen",
    ownerName: "Owen Wright",
    ownerInitials: "OW",
    ownerOrganization: "Thorne",
    visibility: "Shared",
  }),
  task({
    id: "t-process",
    sectionId: "sec-design",
    title: "Confirm business processes",
    startDate: "2026-08-03",
    dueDate: "2026-08-07",
    duration: 5,
    status: "In progress",
    progress: 55,
    ownerId: "usr-priya",
    ownerName: "Priya Shah",
    ownerInitials: "PS",
    ownerOrganization: "Thorne",
    visibility: "Shared",
  }),
  task({
    id: "t-escalation",
    sectionId: "sec-design",
    title: "Define escalation ownership",
    startDate: "2026-08-10",
    dueDate: "2026-08-12",
    duration: 3,
    health: "At risk",
    ownerId: "usr-priya",
    ownerName: "Priya Shah",
    ownerInitials: "PS",
    ownerOrganization: "Thorne",
    visibility: "Shared",
    predecessorIds: ["t-process"],
    blocker: "Final escalation contact has not been confirmed.",
    status: "Blocked",
    commentCount: 3,
  }),
  task({
    id: "t-qa",
    sectionId: "sec-design",
    title: "Finalize QA scorecard",
    startDate: "2026-08-11",
    dueDate: "2026-08-14",
    duration: 4,
    ownerName: "Kim Nacas",
    ownerInitials: "KN",
    visibility: "Internal",
    predecessorIds: ["t-process"],
  }),
  task({
    id: "t-discovery",
    sectionId: "sec-tech",
    type: "summary",
    title: "Discovery",
    startDate: "2026-07-27",
    dueDate: "2026-08-07",
    duration: 10,
    status: "In progress",
    progress: 48,
    ownerName: "Jon Bell",
    ownerInitials: "JB",
    visibility: "Shared",
  }),
  task({
    id: "t-architecture",
    sectionId: "sec-tech",
    parentId: "t-discovery",
    level: 1,
    title: "Review technical architecture",
    startDate: "2026-07-27",
    dueDate: "2026-07-31",
    duration: 5,
    status: "Complete",
    progress: 100,
    ownerName: "Jon Bell",
    ownerInitials: "JB",
    visibility: "Internal",
  }),
  task({
    id: "t-integrations",
    sectionId: "sec-tech",
    parentId: "t-discovery",
    level: 1,
    title: "Review integration requirements",
    startDate: "2026-07-30",
    dueDate: "2026-08-04",
    duration: 4,
    status: "In progress",
    progress: 40,
    health: "At risk",
    ownerName: "Jon Bell",
    ownerInitials: "JB",
    visibility: "Shared",
    predecessorIds: ["t-architecture"],
    fileCount: 2,
  }),
  task({
    id: "t-api",
    sectionId: "sec-tech",
    parentId: "t-discovery",
    level: 1,
    title: "Confirm API requirements",
    startDate: "2026-08-03",
    dueDate: "2026-08-07",
    duration: 5,
    ownerName: "Jon Bell",
    ownerInitials: "JB",
    visibility: "Internal",
    predecessorIds: ["t-integrations"],
  }),
  task({
    id: "t-config",
    sectionId: "sec-tech",
    type: "summary",
    title: "Configuration",
    startDate: "2026-08-10",
    dueDate: "2026-08-24",
    duration: 11,
    ownerName: "Jon Bell",
    ownerInitials: "JB",
    visibility: "Shared",
    predecessorIds: ["t-discovery"],
  }),
  task({
    id: "t-crm",
    sectionId: "sec-tech",
    parentId: "t-config",
    level: 1,
    title: "Configure CRM integration",
    startDate: "2026-08-10",
    dueDate: "2026-08-14",
    duration: 5,
    ownerName: "Jon Bell",
    ownerInitials: "JB",
    visibility: "Internal",
  }),
  task({
    id: "t-telephony",
    sectionId: "sec-tech",
    parentId: "t-config",
    level: 1,
    title: "Configure telephony platform",
    startDate: "2026-08-12",
    dueDate: "2026-08-18",
    duration: 5,
    health: "At risk",
    ownerName: "Jon Bell",
    ownerInitials: "JB",
    visibility: "Shared",
    predecessorIds: ["t-integrations"],
    blocker: "Carrier provisioning date is not confirmed.",
    status: "Blocked",
    commentCount: 4,
  }),
  task({
    id: "t-routing",
    sectionId: "sec-tech",
    parentId: "t-config",
    level: 1,
    title: "Configure routing rules",
    startDate: "2026-08-17",
    dueDate: "2026-08-21",
    duration: 5,
    ownerName: "Jon Bell",
    ownerInitials: "JB",
    visibility: "Internal",
    predecessorIds: ["t-telephony"],
  }),
  task({
    id: "t-testing",
    sectionId: "sec-tech",
    parentId: "t-config",
    level: 1,
    title: "Complete readiness testing",
    startDate: "2026-08-19",
    dueDate: "2026-08-24",
    duration: 4,
    ownerName: "VIPdesk / Thorne",
    ownerInitials: "VT",
    visibility: "Shared",
    predecessorIds: ["t-routing"],
  }),
];

export const files: TaskFile[] = [
  {
    id: "f-plan",
    name: "Implementation plan v3.pdf",
    context: "Approve implementation plan",
    parentType: "task",
    parentId: "t-approve-plan",
    uploadedBy: "Kim Nacas",
    updatedAt: "Today, 8:52 KN",
    size: "1.8 MB",
    type: "PDF",
    visibility: "Shared",
    availabilityState: "Available",
  },
  {
    id: "f-map",
    name: "Integration map.xlsx",
    context: "Review integration requirements",
    parentType: "task",
    parentId: "t-integrations",
    uploadedBy: "Jon Bell",
    updatedAt: "Yesterday",
    size: "842 KB",
    type: "Spreadsheet",
    visibility: "Internal",
    availabilityState: "Available",
  },
  {
    id: "f-carrier",
    name: "Carrier requirements.docx",
    context: "Configure telephony platform",
    parentType: "task",
    parentId: "t-telephony",
    uploadedBy: "Priya Shah",
    updatedAt: "Jul 28",
    size: "324 KB",
    type: "Document",
    visibility: "Shared",
    availabilityState: "Available",
  },
  {
    id: "f-qa",
    name: "QA scorecard draft.xlsx",
    context: "Finalize QA scorecard",
    parentType: "task",
    parentId: "t-qa",
    uploadedBy: "Maria Santos",
    updatedAt: "Jul 27",
    size: "507 KB",
    type: "Spreadsheet",
    visibility: "Internal",
    availabilityState: "Uploading",
  },
];

export const risks: RiskItem[] = [
  {
    id: "r-carrier",
    title: "Carrier provisioning may miss the testing window",
    kind: "Risk",
    impact: "High",
    owner: "Jon Bell",
    dueDate: "2026-08-05",
    state: "Open",
    visibility: "Shared",
    linkedTask: "Configure telephony platform",
    mitigation:
      "Hold a backup testing window and escalate provisioning confirmation.",
  },
  {
    id: "r-contact",
    title: "Escalation contact remains unconfirmed",
    kind: "Issue",
    impact: "Medium",
    owner: "Priya Shah",
    dueDate: "2026-08-03",
    state: "Open",
    visibility: "Shared",
    linkedTask: "Define escalation ownership",
    mitigation: "Client lead to nominate a primary and backup contact.",
  },
  {
    id: "r-staffing",
    title: "Internal training coverage during launch week",
    kind: "Risk",
    impact: "Medium",
    owner: "Maria Santos",
    dueDate: "2026-08-14",
    state: "Watching",
    visibility: "Internal",
    linkedTask: "Training starts",
    mitigation: "Confirm backup facilitator availability.",
  },
];

export const activity: ActivityItem[] = [
  {
    id: "a1",
    actor: "Priya Shah",
    initials: "PS",
    action: "commented on",
    context: "Approve implementation plan",
    timestamp: "Today, 9:14 KN",
    visibility: "Shared",
  },
  {
    id: "a2",
    actor: "Jon Bell",
    initials: "JB",
    action: "flagged a blocker on",
    context: "Configure telephony platform",
    timestamp: "Yesterday, 3:48 PM",
    visibility: "Shared",
  },
  {
    id: "a3",
    actor: "Kim Nacas",
    initials: "KN",
    action: "updated the forecast for",
    context: "Technology ready",
    timestamp: "Yesterday, 1:22 PM",
    visibility: "Shared",
  },
  {
    id: "a4",
    actor: "Maria Santos",
    initials: "MS",
    action: "uploaded a new version to",
    context: "Finalize QA scorecard",
    timestamp: "Jul 27, 4:06 PM",
    visibility: "Internal",
  },
];

export const notifications: NotificationItem[] = [
  {
    id: "n1",
    title: "You were mentioned",
    body: "Priya asked you to confirm the reporting review.",
    projectName: "Thorne implementation",
    timestamp: "9 minutes ago",
    read: false,
    kind: "mention",
    taskId: "t-approve-plan",
  },
  {
    id: "n2",
    title: "An upstream date changed",
    body: "Review integration requirements moved by two working days.",
    projectName: "Thorne implementation",
    timestamp: "Yesterday",
    read: false,
    kind: "schedule",
    taskId: "t-integrations",
  },
  {
    id: "n3",
    title: "Blocker needs an owner",
    body: "Carrier provisioning may affect readiness testing.",
    projectName: "Thorne implementation",
    timestamp: "Yesterday",
    read: true,
    kind: "risk",
    taskId: "t-telephony",
  },
];

const safe = <T extends { visibility: Visibility }>(
  items: T[],
  kind: "internal" | "admin" | "client",
) =>
  kind === "client"
    ? items.filter((item) => item.visibility === "Shared")
    : items;

export function demoBundle(
  kind: "internal" | "admin" | "client",
): ProjectBundle {
  const visibleTasks = safe(tasks, kind);
  return {
    project: projects[0]!,
    sections:
      kind === "client"
        ? sections.filter((section) =>
            visibleTasks.some((task) => task.sectionId === section.id),
          )
        : sections,
    tasks: visibleTasks,
    participants:
      kind === "client"
        ? participants.filter(
            (person) =>
              person.kind === "client" || person.role === "Project owner",
          )
        : participants,
    files: safe(files, kind),
    risks: safe(risks, kind),
    activity: safe(activity, kind),
  };
}
